import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchGrammarSubTopicById } from "./grammarService";
import TopicContentRenderer from "../topic-content/TopicContentRenderer";
import "./GrammarSubTopicDetailPage.css";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const toPublicUrl = (url) => url?.replace(/\/api\/media-images\//, "/api/public/media-images/") ?? url;
const toImgSrc = (url) => !url ? "" : toPublicUrl(url.startsWith("http") ? url : `${BASE_ROOT}${url}`);

/**
 * Splits flat HTML by <h2> tags into numbered blocks.
 * Block index starts at 1 to match image sortOrder.
 * Returns [{ blockIndex, html }]
 */
const splitByH2 = (html) => {
  if (!html) return [];
  const parts = html.split(/(?=<h2[\s>])/i);
  return parts
    .map((part, i) => ({ blockIndex: i + 1, html: part.trim() }))
    .filter((b) => b.html);
};

const GrammarSubTopicDetailPage = () => {
  const { subTopicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backUrl = location.state?.backUrl;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await fetchGrammarSubTopicById(subTopicId);
      if (res.status === 200 && res.data?.status) setData(res.data.data);
      setLoading(false);
    };
    load();
  }, [subTopicId]);

  if (loading) return (
    <div className="gsd-shell">
      <Navbar />
      <div className="gsd-page">
        <div className="gsd-state"><span className="gsd-spinner" /><p>Loading…</p></div>
      </div>
      <Footer />
    </div>
  );

  if (!data) return (
    <div className="gsd-shell">
      <Navbar />
      <div className="gsd-page">
        <div className="gsd-state">
          <span style={{ fontSize: "2.5rem" }}>📭</span>
          <p>Sub-topic not found.</p>
        </div>
      </div>
      <Footer />
    </div>
  );

  const images = data.images || [];
  const useNewContent = !!data.content;

  return (
    <div className="gsd-shell">
      <Navbar />
      <div className="gsd-page">

        <button className="gsd-back-btn" onClick={() => navigate(backUrl || `/study/grammar/${data.grammarTopicId}`)}>
          ← Back to {data.grammarTopicTitle || "Topic"}
        </button>

        {/* ── Hero ── */}
        <div className="gsd-hero">
          {data.coverImageUrl ? (
            <img src={toImgSrc(data.coverImageUrl)} alt={data.title} className="gsd-hero-img" />
          ) : (
            <div className="gsd-hero-placeholder" />
          )}
          <div className="gsd-hero-overlay">
            <span className="gsd-hero-breadcrumb">{data.grammarTopicTitle}</span>
            <h1 className="gsd-hero-title">{data.title}</h1>
          </div>
        </div>

        {/* ── New content mode ── */}
        {useNewContent && (() => {
          const blocks = splitByH2(data.content);
          return (
            <>
              {blocks.map(({ blockIndex, html }) => (
                <React.Fragment key={blockIndex}>
                  <div
                    className="gsd-content-card gsd-html-content"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                  {images
                    .filter((img) => img.sortOrder === blockIndex)
                    .map((img) => (
                      <div key={img.id} className="gsd-interleave-img">
                        <img src={toImgSrc(img.url)} alt={img.altText || ""} />
                        {img.altText && <p className="gsd-img-caption">{img.altText}</p>}
                      </div>
                    ))}
                </React.Fragment>
              ))}
              {/* Images with sortOrder beyond block count */}
              {images
                .filter((img) => img.sortOrder > blocks.length)
                .map((img) => (
                  <div key={img.id} className="gsd-interleave-img">
                    <img src={toImgSrc(img.url)} alt={img.altText || ""} />
                    {img.altText && <p className="gsd-img-caption">{img.altText}</p>}
                  </div>
                ))}
            </>
          );
        })()}

        {/* ── Legacy fallback ── */}
        {!useNewContent && data.sections?.map((sec, idx) => (
          <React.Fragment key={sec.id ?? idx}>
            <div className="gsd-content-card">
              <h2 className="gsd-section-heading">{sec.heading}</h2>
              <div
                className="gsd-section-content gsd-html-content"
                dangerouslySetInnerHTML={{ __html: sec.content }}
              />

              {sec.tableData && (
                <div className="gsd-table-wrap">
                  {sec.tableData.title && <p className="gsd-table-title">{sec.tableData.title}</p>}
                  <table className="gsd-table">
                    <thead>
                      <tr>{sec.tableData.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {sec.tableData.rows.map((row, rIdx) => (
                        <tr key={rIdx}>{row.map((cell, cIdx) => <td key={cIdx}>{cell}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {sec.subSections?.length > 0 && (
                <div className="gsd-subsections">
                  {sec.subSections.map((ss, sIdx) => (
                    <div key={ss.id ?? sIdx} className="gsd-subsection">
                      <h3 className="gsd-subsection-heading">{ss.heading}</h3>
                      <div
                        className="gsd-html-content"
                        dangerouslySetInnerHTML={{ __html: ss.content }}
                      />
                      {ss.tableData && (
                        <div className="gsd-table-wrap">
                          {ss.tableData.title && <p className="gsd-table-title">{ss.tableData.title}</p>}
                          <table className="gsd-table">
                            <thead>
                              <tr>{ss.tableData.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {ss.tableData.rows.map((row, rIdx) => (
                                <tr key={rIdx}>{row.map((cell, cIdx) => <td key={cIdx}>{cell}</td>)}</tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}

        {/* ── YouTube video ── */}
        {data.youtubeLink && (
          <div className="gsd-video-card">
            <h3 className="gsd-video-heading">🎬 Video Lesson</h3>
            <div className="gsd-video-wrapper">
              <iframe
                src={data.youtubeLink
                  .replace("watch?v=", "embed/")
                  .replace("youtu.be/", "youtube.com/embed/")}
                title={data.title}
                allowFullScreen
                frameBorder="0"
              />
            </div>
          </div>
        )}

        {data.createdAt && (
          <p className="gsd-meta">
            Added on {new Date(data.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </p>
        )}

        <TopicContentRenderer topicType="GRAMMAR_SUB_TOPIC" topicId={data.id} />

      </div>
      <Footer />
    </div>
  );
};

export default GrammarSubTopicDetailPage;
